import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import {
  useFormContext,
} from 'react-hook-form';
import ToolTip from 'core/components/Tooltip';
import FileDragOverlayProvider from 'core/context/fileDragOverlay';
import FilePicker from 'pages/Dashboard/components/FilePicker';
import MediaPreview from 'pages/Dashboard/components/MediaPreview';
import UploadIcon from 'core/icons/upload';
import Button from 'pages/Dashboard/components/Button';
import {
  generateDropzoneAccept,
} from 'core/mimeMap';
import {
  useNotify,
} from 'core/hooks';
import {
  mediaEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useGetAllowedFileTypes,
} from 'pages/Dashboard/hooks/media';
import {
  MultimediaType,
} from 'pages/Dashboard/utils/constants';
import useAssociatedInsuranceCards from 'views/EMR/components/Insurance/Dialog/AssociatedInsuranceCardsPanel/useAssociatedInsuranceCards';
import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import {
  toLocalUrls,
} from 'utils/files';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  InsuranceDataForm,
} from 'views/EMR/components/Insurance/Dialog/InsuranceForm';
import UnassociatedInsuranceCardSelect
  from 'views/EMR/components/Insurance/UnassociatedInsuranceCardSelect';

type Props = {
  className?: string;
  insuranceId?: number;
  patientId?: number;
  open: boolean;
};

const actionIconClassName = '!w-3 !h-3';
const actionButtonClassName = [
  '!px-2',
  '!h-6',
  '!text-gray-600',
  '!rounded-sm',
  '!normal-case',
  'flex',
  'items-center',
  'gap-1',
  'whitespace-nowrap',
].join(' ');

function EmptyPreviewState() {
  return (
    <div className="flex min-h-full w-full flex-1 flex-col items-center justify-center gap-3 text-center">
      <div className="rounded-full border border-gray-200 bg-white p-3">
        <ArticleOutlinedIcon className="!h-6 !w-6 !text-gray-400" />
      </div>
      <div className="space-y-1">
        <div className="text-sm font-semibold text-gray-700">No cards added</div>
        <div className="text-xs text-gray-500">
          Upload or capture an insurance card to preview it here.
        </div>
      </div>
    </div>
  );
}

export default function AssociatedInsuranceCardsPanel({
  className,
  insuranceId,
  patientId,
  open,
}: Props) {
  const notify = useNotify();
  const { setValue } = useFormContext<InsuranceDataForm>();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [
    selectExistingAnchorEl,
    setSelectExistingAnchorEl,
  ] = useState<HTMLElement | null>(null);
  const {
    cards,
    appendCards,
    appendExistingCards,
    removeCard,
    isLoadingExistingCards,
    existingMediaIds,
    newUploadFiles,
    shouldDirtyFormState,
  } = useAssociatedInsuranceCards({
    insuranceId,
    open,
  });
  const allowedFileTypes = useGetAllowedFileTypes(MultimediaType.InsuranceCard);
  const cleanExtensions = useMemo(
    () => allowedFileTypes.split(',')
      .map((extension) => extension.trim().replace(/^\./, ''))
      .filter(Boolean),
    [allowedFileTypes],
  );
  const dropzoneAccept = useMemo(
    () => generateDropzoneAccept(cleanExtensions),
    [cleanExtensions],
  );

  useEffect(() => {
    setValue('attachmentState', {
      existingMediaIds,
      newUploadFiles,
    }, {
      shouldDirty: shouldDirtyFormState,
    });
  }, [
    existingMediaIds,
    newUploadFiles,
    setValue,
    shouldDirtyFormState,
  ]);

  return (
    <div
      ref={panelRef}
      className={clsx(
        [
          'relative flex min-h-[16rem] w-full min-w-0 flex-col lg:min-h-0 lg:w-[29rem] lg:min-w-[29rem]',
          'overflow-hidden rounded-lg border border-gray-200 [contain:size]',
        ],
        className,
      )}
    >
      <FileDragOverlayProvider
        targetRef={panelRef}
        onDropFile={async (file: File | File[]) => {
          const localFiles = await toLocalUrls(Array.isArray(file) ? file : [file]);
          appendCards(localFiles);
        }}
        onReject={(reason) => {
          if (reason === 'type') {
            notify({
              variant: 'error',
              message: 'This type of file is not supported.',
            });
          }
        }}
        messages={{
          default: 'Drop your insurance card to add',
          uploading: 'Uploading insurance card...',
          supportedHint: '',
        }}
        backgroundClassName="bg-white/80"
        dropzoneAccept={dropzoneAccept}
        multiple
        animate
        renderOverlayContent={({ isError, message }) => (
          <div className="relative flex flex-row items-center gap-2 px-2 py-1 bg-white/60">
            <UploadIcon
              className={clsx(
                '!w-5 !h-5',
                { 'text-red-500': isError, 'text-blue-500': !isError },
              )}
            />
            <div className="flex flex-col items-start text-left">
              <div
                className={clsx(
                  'text-xs font-semibold',
                  isError ? 'text-red-700' : 'text-gray-800',
                )}
              >
                {message}
              </div>
            </div>
          </div>
        )}
      >
        <div className="flex h-full min-h-0 flex-col">
          <div className="flex items-center justify-between gap-4 px-2 py-2">
            <div className="text-sm font-semibold text-gray-900">
              Associated Insurance Cards
            </div>
            <div className="flex items-center gap-2">
              <FilePicker
                onFileUpload={appendCards}
                allowedFileTypes={allowedFileTypes}
                multiple
                variant="outlined"
                color="secondary"
                className={actionButtonClassName}
              >
                <UploadIcon className={actionIconClassName} />
              </FilePicker>
              <ToolTip title="Capture from Webcam">
                <span>
                  <Button
                    variant="outlined"
                    color="secondary"
                    className={actionButtonClassName}
                    onClick={() => {
                      mediaEventChannel.emit('onStartWebcamScan', {
                        captureType: 'insurance-card',
                        onCapturedFilesConfirmed: appendCards,
                      });
                    }}
                  >
                    <AddAPhotoOutlinedIcon className="!w-4 !h-4" />
                  </Button>
                </span>
              </ToolTip>
              <ToolTip title="Select Unassociated Cards">
                <span>
                  <Button
                    variant="outlined"
                    color="secondary"
                    className={actionButtonClassName}
                    disabled={(patientId ?? 0) <= 0}
                    onClick={(event) => {
                      setSelectExistingAnchorEl(event.currentTarget);
                    }}
                  >
                    <DescriptionOutlinedIcon className="!w-4 !h-4" />
                  </Button>
                </span>
              </ToolTip>
              {!isNil(selectExistingAnchorEl) && (
                <UnassociatedInsuranceCardSelect
                  patientId={patientId}
                  anchorEl={selectExistingAnchorEl}
                  disabledMediaIds={existingMediaIds}
                  onClose={() => {
                    setSelectExistingAnchorEl(null);
                  }}
                  onConfirm={appendExistingCards}
                />
              )}
            </div>
          </div>
          <div className="flex min-h-0 flex-1 flex-col gap-2 border-t border-gray-200 px-2 py-2">
            <div className="flex min-h-0 flex-1 flex-col gap-3">
              <div className="flex min-h-0 flex-1 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50">
                {isLoadingExistingCards && (
                  <div className="flex min-h-full w-full items-center justify-center">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Spinner size={20} />
                      Loading cards...
                    </div>
                  </div>
                )}
                {!isLoadingExistingCards && cards.length > 0 && (
                  <div className="flex w-full flex-col gap-3 p-2">
                    {cards.map((card) => (
                      <div
                        key={card.id}
                        className="h-[18rem] shrink-0 overflow-hidden rounded-md border border-gray-200 bg-white"
                      >
                        <MediaPreview
                          media={card.media}
                          className="h-full w-full"
                          thumbnail
                          classes={{ image: '!object-contain' }}
                          onDelete={() => {
                            removeCard(card.id);
                          }}
                        />
                      </div>
                    ))}
                  </div>
                )}
                {!isLoadingExistingCards && cards.length === 0 && (
                  <EmptyPreviewState />
                )}
              </div>
            </div>
          </div>
        </div>
      </FileDragOverlayProvider>
    </div>
  );
}
